import type { ReactNode } from "react";
import { MdClose } from "react-icons/md";
import BaseButton from "./BaseButton";
import Text from "./Text";
import { useTranslation } from "react-i18next";
// 本文はバンドルに同梱する (開いてから fetch すると空のカード→本文の順に描画され、表示が一瞬崩れるため)
import informationJa from "@/content/information.md?raw";
import informationEn from "@/content/information-en.md?raw";

interface InformationModalProps {
  isOpen: boolean;
  onClose: () => void;
  language?: "ja" | "en";
  isMobile?: boolean;
}

type MarkdownSection =
  | {
      type: "heading";
      content: string;
      level?: number;
    }
  | {
      type: "paragraph";
      content: string;
    }
  | {
      type: "image";
      alt: string;
      src: string;
    };

const parseMarkdown = (markdown: string): MarkdownSection[] => {
  const lines = markdown.split(/\r?\n/);
  const sections: MarkdownSection[] = [];

  let currentParagraph: string[] = [];

  const flushParagraph = () => {
    if (currentParagraph.length > 0) {
      sections.push({
        type: "paragraph",
        content: currentParagraph.join("\n").trim(),
      });
      currentParagraph = [];
    }
  };

  const normalizeImageSrc = (src: string) => {
    if (/^https?:\/\//.test(src)) return src;
    const withoutPublic = src.startsWith("/public/") ? src.replace("/public", "") : src;
    if (withoutPublic.startsWith("/")) return withoutPublic;
    return `/${withoutPublic}`;
  };

  lines.forEach((line) => {
    const headingMatch = line.match(/^(#{1,6})\s+(.+)$/);
    const imageMatch = line.match(/^!\[(.*?)\]\((.*?)\)$/);
    if (headingMatch) {
      flushParagraph();
      sections.push({
        type: "heading",
        content: headingMatch[2],
        level: headingMatch[1].length,
      });
    } else if (imageMatch) {
      flushParagraph();
      const [, alt = "", src = ""] = imageMatch;
      if (src) {
        sections.push({
          type: "image",
          alt,
          src: normalizeImageSrc(src.trim()),
        });
      }
    } else if (line.trim() === "") {
      flushParagraph();
    } else {
      currentParagraph.push(line);
    }
  });

  flushParagraph();
  return sections;
};

const URL_PATTERN = /(https?:\/\/[^\s<]+)/g;
const isUrlText = (value: string) => /^https?:\/\/[^\s<]+$/.test(value);

const renderInlineContent = (content: string, enableLinks: boolean): ReactNode[] => {
  if (!enableLinks) {
    return [content];
  }

  const parts = content.split(URL_PATTERN);

  return parts
    .filter((part) => part.length > 0)
    .map((part, index) =>
      isUrlText(part) ? (
        <a
          key={`link-${index}-${part}`}
          href={part}
          target="_blank"
          rel="noreferrer"
          className="underline underline-offset-2 break-all text-[#005BBB]"
        >
          {part}
        </a>
      ) : (
        <span key={`text-${index}`}>{part}</span>
      ),
    );
};

const renderParagraphContent = (content: string, enableLinks: boolean): ReactNode[] => {
  const parts = content.split(/<br\s*\/?>(?:\s)*/i);

  return parts.reduce<ReactNode[]>((acc, part, index) => {
    if (index > 0) {
      acc.push(<br key={`br-${index}`} />);
    }
    acc.push(...renderInlineContent(part, enableLinks));
    return acc;
  }, []);
};

const InformationModal: React.FC<InformationModalProps> = ({
  isOpen,
  onClose,
  language = "ja",
  isMobile = false,
}) => {
  const { t } = useTranslation();

  const isEnglish = language === "en";
  const content = isEnglish ? informationEn : informationJa;

  if (!isOpen) return null;

  const sections = parseMarkdown(content);

  const getHeadingVariant = (level?: number) => {
    switch (level) {
      case 1:
        return "heading/xl";
      case 2:
        return "heading/l";
      case 3:
        return "heading/m";
      default:
        return "heading/s";
    }
  };

  const renderedSections: ReactNode[] = [];

  for (let i = 0; i < sections.length; i++) {
    const section = sections[i];

    if (section.type === "heading") {
      if (isMobile && i === 1) {
        continue;
      }
      const isAfterImage = sections[i - 1]?.type === "image";
      renderedSections.push(
        <Text
          key={`heading-${i}`}
          variant={getHeadingVariant(section.level)}
          as={`h${section.level}` as React.ElementType}
          className={`${isAfterImage ? "-mt-12 mb-2" : "mb-4"} ${
            isEnglish ? "text-[20px] sm:text-[28px] leading-[1.5]" : ""
          }`}
        >
          {section.content}
        </Text>,
      );
      continue;
    }

    if (section.type === "image") {
      const nextSection = sections[i + 1];
      const isQrImage = /qr/i.test(section.alt) || section.src.includes("feedback_qr");
      const isFindsLogo = section.src.includes("finds-logo");
      if (isMobile && isQrImage) {
        continue;
      }
      const imageSize = isQrImage ? 140 : isFindsLogo ? 260 : 200;
      const imageClass = isQrImage
        ? "w-28 h-28 object-contain"
        : isFindsLogo
          ? "w-52 h-52 object-contain"
          : "w-40 h-40 object-contain";

      if (nextSection?.type === "paragraph") {
        renderedSections.push(
          <div key={`image-with-text-${i}`} className="flex flex-col items-start gap-2 mb-4">
            <img
              src={section.src}
              alt={section.alt}
              width={imageSize}
              height={imageSize}
              className={imageClass}
            />
            {isMobile ? (
              <p className="select-text break-words whitespace-pre-wrap text-[13px] leading-[1.8] text-TextPrimary">
                {renderParagraphContent(nextSection.content, true)}
              </p>
            ) : (
              <Text
                variant="body/s"
                as="p"
                className={`break-words ${
                  isEnglish ? "text-[13px] sm:text-[15px] leading-[1.6]" : ""
                }`}
              >
                {renderParagraphContent(nextSection.content, false)}
              </Text>
            )}
          </div>,
        );
        i += 1;
        continue;
      }

      renderedSections.push(
        <div key={`image-${i}`} className="flex justify-start">
          <img
            src={section.src}
            alt={section.alt}
            width={imageSize}
            height={imageSize}
            className={imageClass}
          />
        </div>,
      );
      continue;
    }

    if (isMobile) {
      renderedSections.push(
        <p
          key={`paragraph-${i}`}
          className={`mb-4 select-text break-words whitespace-pre-wrap text-[14px] leading-[1.8] text-TextPrimary ${
            isEnglish ? "sm:text-[15px] sm:leading-[1.6]" : ""
          }`}
        >
          {renderParagraphContent(section.content, true)}
        </p>,
      );
      continue;
    }

    renderedSections.push(
      <Text
        key={`paragraph-${i}`}
        variant="body/m"
        as="p"
        className={`mb-4 ${isEnglish ? "text-[13px] sm:text-[15px] leading-[1.6]" : ""}`}
      >
        {renderParagraphContent(section.content, false)}
      </Text>,
    );
  }

  return (
    // backdrop-blur は地図上で重いため使わない (全画面だと現在地マーカーの点滅で毎フレーム再計算になる)
    <div
      className={`fixed inset-0 z-[100] flex ${
        isMobile
          ? "h-full w-full items-stretch justify-stretch bg-white"
          : "h-screen w-screen items-center justify-center bg-black/50"
      }`}
      onClick={onClose}
    >
      <div
        className={`relative flex flex-col overflow-hidden bg-white ${
          isMobile
            ? "h-full w-full max-h-none max-w-none rounded-none"
            : "max-h-[calc(100dvh-24px)] w-7xl max-w-[calc(100vw-24px)] rounded-4xl shadow-lg sm:max-h-[calc(100vh-24px)]"
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={`absolute right-4 top-4 z-10 ${isMobile ? "" : "sm:right-6 sm:top-6"}`}>
          <BaseButton onClick={onClose} icon={MdClose}>
            {t("閉じる")}
          </BaseButton>
        </div>
        <div
          className={`min-h-0 flex-1 overflow-y-auto overscroll-contain ${
            isMobile ? "px-4 pb-6 pt-2" : "px-8 pb-8 pt-2 sm:px-12 sm:pb-4 sm:pt-2"
          }`}
        >
          {renderedSections}
        </div>
      </div>
    </div>
  );
};

export default InformationModal;
